import { ExternalLink, Mail, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trackWelcomeOptionSelected } from '../utils/analytics';
import phoneComputerImage from '../assets/0c05356776c0822e039e635fb6a9f68c1adefd25.png';

interface WelcomeMobileInstructionsProps {
  onEmailLink: () => void;
  onOpenConsultation: () => void;
  onStart: () => void;
}

export function WelcomeMobileInstructions({ onEmailLink, onOpenConsultation, onStart }: WelcomeMobileInstructionsProps) {
  const { t } = useTranslation('welcome');

  // Get consultation ID from root element
  const consultationId = (() => {
    const rootElement = document.getElementById('consultation-wizard-root');
    return rootElement?.getAttribute('data-consultation-id') || 'unknown';
  })();

  // Handle start button clicks with option tracking
  const handleStartOptionA = () => {
    trackWelcomeOptionSelected('option_a_phone', consultationId);
    onStart();
  };

  const handleStartOptionB = () => {
    trackWelcomeOptionSelected('option_b_mobile', consultationId);
    onStart();
  };

  return (
    <div className="space-y-6 mb-6">
      {/* Option A - Email Link */}
      <div className="bg-white p-6 border border-[#FFD4C7] rounded">
        <h3 className="font-bold text-[18px] mb-4">{t('instructions.mobile.optionA.title')}</h3>
        <img 
          src={phoneComputerImage}
          alt={t('instructions.mobile.optionA.imageAlt')}
          className="w-full h-auto object-contain rounded mb-4"
        />
        <ol className="list-decimal list-inside space-y-3 mb-4 text-[16px]">
          <li>{t('instructions.mobile.optionA.step1')}</li>
          <li>{t('instructions.mobile.optionA.step2')}</li>
          <li>
            <a 
              href="https://webgate.ec.europa.eu/cas/login" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[#4D2200] underline"
            >
              {t('instructions.mobile.optionA.step3.link')}
            </a>
            {t('instructions.mobile.optionA.step3.text')}
          </li>
        </ol>
        <div className="space-y-3 mb-3">
          <button
            onClick={onEmailLink}
            className="w-full bg-[#FF6F4E] text-black hover:bg-[#FF6F4E]/90 px-6 font-bold text-[16px] inline-flex items-center justify-center gap-2 transition-colors min-h-[48px]"
          >
            <Mail className="w-5 h-5" />
            {t('instructions.mobile.optionA.emailButton')}
          </button>
          <button
            onClick={handleStartOptionA}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 font-bold text-[16px] inline-flex items-center justify-center gap-2 transition-colors min-h-[48px]"
          >
            <Play className="w-5 h-5" />
            {t('instructions.mobile.optionA.startButton')}
          </button>
        </div>
        <p className="text-[16px] text-[#0A1402] text-center">
          {t('instructions.mobile.optionA.recommended')}
        </p>
      </div>

      {/* Option B - Continue on Mobile */}
      <div className="bg-white p-6 border border-[#FFD4C7] rounded">
        <h3 className="font-bold text-[18px] mb-4">{t('instructions.mobile.optionB.title')}</h3>
        <ol className="list-decimal list-inside space-y-3 mb-4 text-[16px]">
          <li>
            <a 
              href="https://webgate.ec.europa.eu/cas/login" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[#4D2200] underline"
            >
              {t('instructions.mobile.optionB.step1.link')}
            </a>
            {t('instructions.mobile.optionB.step1.text')}
          </li>
          <li>{t('instructions.mobile.optionB.step2')}</li>
          <li>{t('instructions.mobile.optionB.step3')}</li>
          <li>{t('instructions.mobile.optionB.step4')}</li>
          <li>{t('instructions.mobile.optionB.step5')}</li>
        </ol>
        <div className="space-y-3">
          <button
            onClick={onOpenConsultation}
            className="w-full bg-[#FF6F4E] text-black hover:bg-[#FF6F4E]/90 px-6 font-bold text-[16px] inline-flex items-center justify-center gap-2 transition-colors min-h-[48px]"
          >
            <ExternalLink className="w-5 h-5" />
            {t('instructions.mobile.optionB.openConsultation')}
          </button>
          <button
            onClick={handleStartOptionB}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 font-bold text-[16px] inline-flex items-center justify-center gap-2 transition-colors min-h-[48px]"
          >
            <Play className="w-5 h-5" />
            {t('instructions.mobile.optionB.startButton')}
          </button>
        </div>
      </div>
    </div>
  );
}

